import { useCallback } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { Protected } from '@cotera/client/app/components/app/permissioned';
import {
  UserProfile,
  UserSessions,
  UserSecurity,
  UsersManagement,
  WorkOsWidgets,
} from '@workos-inc/widgets';
import { OauthConnectionsSection } from './oauth-connections-section';
import { Loading } from '@cotera/client/app/components';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { Button } from '../components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useTenantedQueryKey } from '@cotera/client/app/hooks/use-tenanted-query-key';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useWarehouseConnection } from '@cotera/client/app/hooks';
import { ConnectWarehouseForm } from '@cotera/client/app/components/warehouse/connect-warehouse-form';
import { PROVIDER_ICONS } from '../components/tool-connections/tool-connections-dialog';
import { startCase } from 'lodash';
import {
  formatProviderAccountStatusLabel,
  providerAccountStatusTone,
} from './provider-account-status';
import { SlackConnectionSection } from './slack-connection-section';
import { useShowToolSettingsModal } from '../components/tool-connections';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useUsage } from '@cotera/client/app/hooks/use-usage';
import {
  BigQueryLogo,
  PostgresLogo,
  RedshiftLogo,
  SnowflakeLogo,
} from '@cotera/client/app/components';

/**
 * Theme the WorkOS widgets (built on Radix Themes) so their buttons, accents and
 * corners match the app: indigo accent (our primary), a slate gray ramp, and a
 * rounded radius close to our --radius. Keeps the embedded widgets from looking
 * like a different product.
 */
const WORKOS_WIDGET_THEME = {
  accentColor: 'indigo',
  grayColor: 'slate',
  radius: 'large',
  panelBackground: 'solid',
} as const;

// WorkOS Widget Views - Consolidated Profile Page
export const ProfileView: React.FC = () => {
  const { getAccessToken, isLoading, user } = useAuth();

  if (isLoading) {
    return <Loading.Shimmer className="h-32 w-full" />;
  }

  if (!user) {
    return <div className="text-muted-foreground">Please sign in</div>;
  }

  return (
    <div className="space-y-8">
      {/* Profile Section */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium">Profile</h3>
        <WorkOsWidgets theme={WORKOS_WIDGET_THEME}>
          <UserProfile authToken={getAccessToken} />
        </WorkOsWidgets>
      </section>

      {/* Security Section */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium">Security</h3>
        <WorkOsWidgets theme={WORKOS_WIDGET_THEME}>
          <UserSecurity authToken={getAccessToken} />
        </WorkOsWidgets>
      </section>

      {/* Sessions Section */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium">Sessions</h3>
        <WorkOsWidgets theme={WORKOS_WIDGET_THEME}>
          <UserSessions authToken={getAccessToken} />
        </WorkOsWidgets>
      </section>
    </div>
  );
};

export const UserManagementView: React.FC = () => {
  const { getAccessToken, isLoading, user } = useAuth();
  const client = useTenantedClient();

  // WorkOS doesn't carry `widgets:users-table:manage` in the AuthKit access
  // token, so the raw token can only read the member list — roles, invite and
  // role-changes all 403. Authenticate the widget with a server-minted widget
  // token that has the scope. If minting fails (e.g. endpoint not deployed yet),
  // fall back to the raw token so the list still reads instead of going blank.
  const getUsersTableToken = useCallback(async (): Promise<string> => {
    const result = await client.workosWidget.usersTableToken();
    if (result.isOk()) {
      return result.value.token;
    }
    // Fall back to the raw token so the list still reads instead of going blank;
    // manage actions (invite, role change) will 403 until the scoped token works.
    // eslint-disable-next-line no-console
    console.error('[UserManagement] widget-token endpoint error', result.error);
    return getAccessToken();
  }, [client, getAccessToken]);

  if (isLoading) {
    return <Loading.Shimmer className="h-32 w-full" />;
  }

  if (!user) {
    return <div className="text-muted-foreground">Please sign in</div>;
  }

  return (
    <WorkOsWidgets theme={WORKOS_WIDGET_THEME}>
      <UsersManagement authToken={getUsersTableToken} />
    </WorkOsWidgets>
  );
};

// Organization View
const useBillingId = () => {
  const key = useTenantedQueryKey(['billingId']);
  const client = useTenantedClient();
  const { data } = useSuspenseQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await client.billingSettings.getOrbExternalId({});
      if (res.isErr()) {
        return { orbExternalId: null };
      }
      return res.value;
    },
  });

  return data.orbExternalId;
};

// Reusable field component for settings
const SettingsField: React.FC<{
  label: string;
  value: string | null;
  copyable?: boolean;
  placeholder?: string;
}> = ({ label, value, copyable = false, placeholder = 'Not set' }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </label>
    <div className="flex items-center justify-between rounded-lg border border-border bg-white px-3 py-2.5">
      <span className="text-sm font-mono text-foreground">
        {value ?? placeholder}
      </span>
      {copyable && value !== null && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => navigator.clipboard.writeText(value)}
        >
          <Icon icon="copy" />
        </Button>
      )}
    </div>
  </div>
);

const BillingIdField: React.FC = () => {
  const billingId = useBillingId();

  return (
    <SettingsField
      label="Billing ID"
      value={billingId}
      copyable={billingId !== null}
      placeholder="Contact support"
    />
  );
};

const SuperadminDebugInfo: React.FC = () => {
  const org = useWhoami((s) => s.org);
  const whoami = useWhoami((s) => s.whoami);
  const {
    isLoading,
    user,
    role,
    roles,
    organizationId,
    permissions,
    featureFlags,
    impersonator,
  } = useAuth();

  const authState = {
    isLoading,
    user,
    role,
    roles,
    organizationId,
    permissions,
    featureFlags,
    impersonator,
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Debug Info (Superadmin)
      </h3>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Org Object
          </label>
          <pre className="rounded-lg border border-border bg-white p-3 text-xs font-mono text-foreground overflow-auto max-h-64">
            {JSON.stringify(org, null, 2)}
          </pre>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Whoami
          </label>
          <pre className="rounded-lg border border-border bg-white p-3 text-xs font-mono text-foreground overflow-auto max-h-64">
            {JSON.stringify(whoami, null, 2)}
          </pre>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            WorkOS Auth
          </label>
          <pre className="rounded-lg border border-border bg-white p-3 text-xs font-mono text-foreground overflow-auto max-h-64">
            {JSON.stringify(authState, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export const OrganizationView: React.FC = () => {
  const orgId = useWhoami((s) => s.org.id);
  const orgName = useWhoami((s) => s.org.name);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SettingsField label="Organization Name" value={orgName} />
        <SettingsField label="Organization ID" value={orgId} copyable />
      </div>
      <BillingIdField />
      <Protected scopes={[['use', 'superadmin']]}>
        <SuperadminDebugInfo />
      </Protected>
    </div>
  );
};

// Plan hook - exported for use in billing
export const usePlan = () => {
  const key = useTenantedQueryKey(['plan']);
  const client = useTenantedClient();
  const { data } = useSuspenseQuery({
    queryKey: key,
    queryFn: async () => {
      return Assert.assertOk(await client.org.getPlan({}));
    },
  });

  return data;
};

// Rate Limits View
const useLimits = () => {
  const key = useTenantedQueryKey(['limits']);
  const client = useTenantedClient();
  const { data } = useSuspenseQuery({
    queryKey: key,
    queryFn: async () => {
      return Assert.assertOk(await client.atc.allocator.getLimits({}));
    },
  });
  return data;
};

export const RateLimitsView: React.FC = () => {
  const limits = useLimits();
  const usage = useUsage();

  const limitsWithUsage = limits
    .map((limit) => {
      const remaining =
        usage.find(
          (x) => x.resource === limit.resource && x.period === limit.period
        )?.remaining ?? limit.amount;
      const usedAmount = limit.amount - remaining;
      const usedPercentage = Math.round((usedAmount / limit.amount) * 100);
      return { ...limit, usedAmount, usedPercentage };
    })
    .sort((a, b) => b.usedPercentage - a.usedPercentage);

  return (
    <div className="space-y-4">
      {/* Rate Limits Table */}
      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Resource
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                Limit
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Period
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                Usage
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {limitsWithUsage.map((limit) => (
              <TableRow key={limit.resource + limit.period}>
                <TableCell className="font-medium">
                  {startCase(limit.resource)}
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  {limit.amount.toLocaleString()}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  per {limit.period}
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={
                      limit.usedPercentage >= 90
                        ? 'text-destructive'
                        : limit.usedPercentage >= 70
                        ? 'text-amber-600'
                        : 'text-muted-foreground'
                    }
                  >
                    {limit.usedPercentage}%
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Keep PlanView as alias for backwards compatibility
export const PlanView = RateLimitsView;

// Warehouse View
const WAREHOUSE_LOGOS = {
  SNOWFLAKE: SnowflakeLogo,
  BIGQUERY: BigQueryLogo,
  REDSHIFT: RedshiftLogo,
  POSTGRES: PostgresLogo,
};

export const WarehouseView: React.FC = () => {
  const { data: warehouse, refetch } = useWarehouseConnection();

  if (!warehouse) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Icon icon="server" className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold">Connect a warehouse</p>
            <p className="text-sm text-muted-foreground">
              Enter your warehouse credentials to connect it to this
              organization.
            </p>
          </div>
        </div>
        <ConnectWarehouseForm onConnected={() => void refetch()} />
      </div>
    );
  }

  const Logo = WAREHOUSE_LOGOS[warehouse.type as keyof typeof WAREHOUSE_LOGOS];

  return (
    <div className="space-y-6">
      {/* Warehouse Header */}
      <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-background">
          {Logo !== undefined ? (
            <Logo height={28} />
          ) : (
            <Icon icon="server" className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div>
          <p className="font-semibold">{warehouse.displayName}</p>
          <p className="text-sm text-muted-foreground">{warehouse.type}</p>
        </div>
      </div>

      {/* Connection Details */}
      <div className="grid gap-4 sm:grid-cols-2">
        <SettingsField label="Read Schema" value={warehouse.readSchema} />
        <SettingsField label="Write Schema" value={warehouse.writeSchema} />
      </div>
    </div>
  );
};

// Integrations View
const useAllProviders = () => {
  const client = useTenantedClient();
  const queryKey = useTenantedQueryKey((orgId) =>
    queryKeys.toolsV2.allProviders({ orgId })
  );

  return useSuspenseQuery({
    queryKey,
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.listAllProviders({}));
    },
  });
};

export const IntegrationsView: React.FC = () => {
  const { data } = useAllProviders();
  const queryClient = useQueryClient();
  const providersQueryKey = useTenantedQueryKey((orgId) =>
    queryKeys.toolsV2.allProviders({ orgId })
  );
  // Open the tool-connections modal directly. `NewToolAction` isn't registered
  // in the settings modal's Actions provider (it lives in the v0 graph editor
  // and requires a GraphModel in scope), so `useAction(NewToolAction)` here
  // throws "Action not found" — this hook has no such dependency.
  const addIntegration = useShowToolSettingsModal(async () => {
    await queryClient.invalidateQueries({ queryKey: providersQueryKey });
  });

  const sortedProviders = [...data.providers]
    .filter((provider) => provider.key !== 'slack')
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <div className="space-y-8">
      {/* Slack — backed by slack_installations, not WorkOS Pipes */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">Slack</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Install Coco in your workspace for DMs and @mentions.
          </p>
        </div>
        <SlackConnectionSection />
      </section>

      {/* OAuth Connections */}
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">OAuth Connections</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Connect your accounts for tools that require OAuth authentication.
          </p>
        </div>
        <OauthConnectionsSection />
      </section>

      {/* API Key Connections */}
      <section className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium">API Key Connections</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Manage integrations that use API keys for authentication.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => addIntegration()}>
            <Icon icon="plus" className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>

        {sortedProviders.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-8 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Icon icon="key" className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              No API key integrations configured.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => addIntegration()}
            >
              Add your first integration
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[180px] text-xs font-medium uppercase tracking-wide">
                    Provider
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">
                    Account
                  </TableHead>
                  <TableHead className="w-[100px] text-xs font-medium uppercase tracking-wide">
                    Status
                  </TableHead>
                  <TableHead className="w-[120px] text-xs font-medium uppercase tracking-wide text-right">
                    Created
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProviders.flatMap((provider) => {
                  const ProviderIcon =
                    PROVIDER_ICONS[provider.key as keyof typeof PROVIDER_ICONS];
                  return [...provider.accounts]
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center">
                              {ProviderIcon !== undefined ? (
                                <ProviderIcon />
                              ) : (
                                <Icon
                                  icon="puzzle-piece"
                                  className="h-4 w-4 text-muted-foreground"
                                />
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {provider.displayName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {account.displayName}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const tone = providerAccountStatusTone(account);
                            const toneClass =
                              tone === 'built-in' || tone === 'default'
                                ? 'text-green-600'
                                : tone === 'active'
                                ? 'text-blue-600'
                                : 'text-muted-foreground';
                            const dotClass =
                              tone === 'built-in' || tone === 'default'
                                ? 'bg-green-500'
                                : tone === 'active'
                                ? 'bg-blue-500'
                                : 'bg-muted-foreground';

                            return (
                              <span
                                className={`inline-flex items-center gap-1.5 text-xs ${toneClass}`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${dotClass}`}
                                />
                                {formatProviderAccountStatusLabel(account)}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {new Date(account.createdAt).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </TableCell>
                      </TableRow>
                    ));
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
};
